/**
 * Turning a push from hamyan-announce into a notification, and a tap on it
 * into a page.
 *
 * Lives outside `service-worker.ts` for the same reason as `swNavigation.ts`:
 * that file is compiled as a worker and cannot be imported from a test.
 *
 * The payload is announce's `build_payload`:
 *   {"v":1,"id":81,"title":"…","body":"…","url":"/fund/12","tag":"reminder:12",
 *    "category":"reminder","silent":false,"renotify":true,"ts":1758000000000}
 */

export interface PushPayload {
    id: number | null
    title: string
    body: string
    url: string
    tag: string
    category: string
    silent: boolean
    renotify: boolean
    ts: number | null
}

const FALLBACK_TITLE = 'همیان'

/**
 * «ارسال اعلان آزمایشی» on the settings page. The backend tags that push
 * `test-<nonce>` and the service worker tells the page when it has shown it,
 * so the page can say whether it was the delivery or the device that failed.
 */
export const SELF_TEST_TAG_PREFIX = 'test-'
export const WEB_PUSH_SHOWN = 'WEB_PUSH_SHOWN'

export interface WebPushShownMessage {
    type: typeof WEB_PUSH_SHOWN
    tag: string
    // Still in the browser's list of displayed notifications a moment later.
    // false: the OS swallowed it (notifications off for the browser, Focus).
    // null: this browser cannot tell.
    listed: boolean | null
    // showNotification itself refused, e.g. permission gone.
    error: string | null
}

export const isSelfTestTag = (tag: string): boolean =>
    tag.startsWith(SELF_TEST_TAG_PREFIX) && tag.length > SELF_TEST_TAG_PREFIX.length
const HOME = '/home'

/**
 * Only ever a path on our own site. The backend already builds relative paths;
 * this is the last line against a payload that tries to send somebody to
 * another origin from a notification that says «همیان».
 */
export const safePath = (url: unknown, origin: string): string => {
    if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//'))
        return HOME
    try {
        const parsed = new URL(url, origin)
        if (parsed.origin !== origin) return HOME
        return `${parsed.pathname}${parsed.search}`
    } catch {
        return HOME
    }
}

export const parsePushPayload = (
    raw: string | null | undefined
): PushPayload => {
    let data: Record<string, unknown> = {}
    try {
        data = raw ? JSON.parse(raw) : {}
    } catch {
        // A push that is not JSON still has to show *something*: Chrome shows
        // its own «This site has been updated in the background» otherwise,
        // and counts it against us.
        data = { body: raw ?? '' }
    }
    const id = Number(data.id)
    const ts = Number(data.ts)
    return {
        id: Number.isInteger(id) && id > 0 ? id : null,
        title:
            typeof data.title === 'string' && data.title.trim()
                ? data.title
                : FALLBACK_TITLE,
        body: typeof data.body === 'string' ? data.body : '',
        url: typeof data.url === 'string' ? data.url : HOME,
        tag: typeof data.tag === 'string' ? data.tag : '',
        category: typeof data.category === 'string' ? data.category : '',
        silent: data.silent === true,
        renotify: data.renotify === true,
        ts: Number.isFinite(ts) && ts > 0 ? ts : null,
    }
}

export const notificationOptions = (
    payload: PushPayload,
    icon: string,
    badge: string
): NotificationOptions & { renotify?: boolean; timestamp?: number } => ({
    body: payload.body,
    icon,
    badge,
    dir: 'rtl',
    lang: 'fa',
    // Same tag = the newer notification replaces the older one (announce tags
    // reminders per fund), so a phone never stacks five «یادآوری پرداخت».
    tag: payload.tag || undefined,
    // Chrome throws on renotify without a tag.
    renotify: payload.tag ? payload.renotify : false,
    silent: payload.silent,
    ...(payload.ts ? { timestamp: payload.ts } : {}),
    data: { id: payload.id, url: payload.url, category: payload.category },
})

/** The page a tap opens, carrying which notification it was for analytics. */
export const openUrl = (
    data: { id?: unknown; url?: unknown; category?: unknown } | null | undefined,
    origin: string
): string => {
    const path = safePath(data?.url, origin)
    const target = new URL(path, origin)
    const id = Number(data?.id)
    if (Number.isInteger(id) && id > 0) target.searchParams.set('pn', String(id))
    if (typeof data?.category === 'string' && data.category)
        target.searchParams.set('pc', data.category)
    return `${target.pathname}${target.search}`
}
